import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import mappers from "./mappers";

import { FILTERS } from "@common/constants";
import { useLocalStorage } from "@services/common/hooks/useLocalStorage";
import { DeviceFilters } from "@services/devices/types";
import { MeetingInstanceFilters } from "@services/meetings/types";
import { RoomsFilters } from "@services/rooms/types";
import { TicketsFilters } from "@services/tickets/types";
import { useCustomSearchParams } from "@services/common/hooks/useSearchParams";
import { TIME_FILTER_SEARCH_KEY as MEETING_TIME_FILTER_SEARCH_KEY } from "./mappers/meetings";
import { TIME_FILTER_SEARCH_KEY as TICKET_TIME_FILTER_SEARCH_KEY } from "./mappers/tickets";
import { SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY } from "./mappers/rooms";
import {
  LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
  WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY,
} from "./mappers/devices";
import { SearchFilterMode } from "@common/hooks/useSearchFilterMode";
import { getSearchTypeFromLocation } from "@common/components/Header/SearchBar/Search/hook/useInitializeSearchType";

type TimeFilterKeys = {
  [MEETING_TIME_FILTER_SEARCH_KEY]?: string;
  [TICKET_TIME_FILTER_SEARCH_KEY]?: string;
  [SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY]?: string;
  [LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY]?: string;
  [WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY]?: string;
};

type AnyFilter = (
  | RoomsFilters
  | DeviceFilters
  | MeetingInstanceFilters
  | TicketsFilters
) &
  TimeFilterKeys;

export type LocalStorageFilters = {
  deviceFilters?: FilterDefinition[];
  roomFilters?: FilterDefinition[];
  meetingFilters?: FilterDefinition[];
  ticketFilters?: FilterDefinition[];
  accountFilters?: FilterDefinition[];
  locationFilters?: FilterDefinition[];
  workflowFilters?: FilterDefinition[];
  userFilters?: FilterDefinition[];
  triggerFilters?: FilterDefinition[];
  auditFilters?: FilterDefinition[];
  deviceCatalogFilters?: FilterDefinition[];
  countryFilters?: FilterDefinition[];
};
export const DEFAULT_USER_FILTERS = {
  deviceFilters: [],
  roomFilters: [],
  meetingFilters: [],
  ticketFilters: [],
  accountFilters: [],
  locationFilters: [],
  workflowFilters: [],
  triggerFilters: [],
  auditFilters: [],
  deviceCatalogFilters: [],
  countryFilters: [],
};

export const FiltersTypeImpl: { [k in FiltersType]: k } = {
  Devices: "Devices",
  Rooms: "Rooms",
  Meetings: "Meetings",
  Tickets: "Tickets",
  Accounts: "Accounts",
  Countries: "Countries",
  Locations: "Locations",
  Workflows: "Workflows",
  Users: "Users",
  Triggers: "Triggers",
  AuditTrails: "AuditTrails",
  DeviceCatalog: "DeviceCatalog",
};

export const useFilters = (
  type: UserWidgetType
): [FilterDefinition[], (filters: FilterDefinition[]) => void] => {
  const [searchParams, setSearchParams] = useCustomSearchParams();
  const { state } = useLocation();
  const searchFilters =
    mappers[type].fromSearchParams.toDefinition(searchParams);
  const searchTypeFromLocation = getSearchTypeFromLocation();
  const setFilters = useCallback(
    (filters: FilterDefinition[]) => {
      const searchFilters: AnyFilter = mappers[type].fromSearchParams.toSL(
        searchParams,
        false
      );

      let timeFilterKeys: (keyof TimeFilterKeys)[] = [];
      switch (type) {
        case "Meetings":
          timeFilterKeys = [MEETING_TIME_FILTER_SEARCH_KEY];
          break;
        case "Tickets":
          timeFilterKeys = [TICKET_TIME_FILTER_SEARCH_KEY];
          break;
        case "Rooms":
          timeFilterKeys = [SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY];
          break;
        case "Devices":
          timeFilterKeys = [
            LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
            WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY,
          ];
          break;
        default:
          break;
      }

      const merged: any = mappers[type].fromDefinition.toSL(filters, false);
      timeFilterKeys.forEach((timeFilterKey) => {
        merged[timeFilterKey] = searchFilters[timeFilterKey];
        const previousTimeFilter = searchParams.get(timeFilterKey);

        if (previousTimeFilter) {
          searchFilters[timeFilterKey] = previousTimeFilter;
        }

        /**
         * Specifically handles the time filter.
         */
        const timeFilter = filters?.find(
          ({ filterType }) => (filterType as FilterTypes) === timeFilterKey
        );

        if (timeFilter !== undefined) {
          merged[timeFilterKey] = timeFilter.filterValue;
          if (timeFilter.filterValue !== "Custom time period") {
            switch (timeFilterKey) {
              case MEETING_TIME_FILTER_SEARCH_KEY:
              case TICKET_TIME_FILTER_SEARCH_KEY:
                delete merged["start.after"];
                delete merged["end.before"];
                break;
              case SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY:
                delete merged["details.serviceExpirationDate.after"];
                delete merged["details.serviceExpirationDate.before"];
                break;
              case LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY:
                delete merged["details.leaseExpirationDate.after"];
                delete merged["details.leaseExpirationDate.before"];
                break;
              case WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY:
                delete merged["details.warrantyExpirationDate.after"];
                delete merged["details.warrantyExpirationDate.before"];
                break;
              default:
                break;
            }
          }
        } else {
          delete merged[timeFilterKey];
        }
      });

      const filterParams = mappers[type].fromSL.toSearchParams(merged as any);

      const fromAccount = searchParams.get("fromAccount");
      const fromCountry = searchParams.get("fromCountry");
      const fromLocation = searchParams.get("fromLocation");
      const fromRoom = searchParams.get("fromRoom");
      const orderBy = searchParams.get("orderBy");
      const tab = searchParams.get("tab");
      if (fromAccount) filterParams.append("fromAccount", fromAccount);
      if (fromCountry) filterParams.append("fromCountry", fromCountry);
      if (fromLocation) filterParams.append("fromLocation", fromLocation);
      if (fromRoom) filterParams.append("fromRoom", fromRoom);
      if (orderBy) filterParams.append("orderBy", orderBy);
      if (tab) filterParams.append("tab", tab);
      if (
        filterParams.getAll.length > 0 &&
        searchTypeFromLocation !== undefined
      )
        filterParams.append("mode", SearchFilterMode.FILTER);
      setSearchParams(filterParams, { replace: true, state });
    },
    [searchFilters, searchParams]
  );
  return [searchFilters, setFilters];
};
export const useOtherFilters = (
  type: UserOtherType
): [FilterDefinition[], (filters: FilterDefinition[]) => void] => {
  const [searchParams, setSearchParams] = useCustomSearchParams();
  const { state } = useLocation();
  const searchTypeFromLocation = getSearchTypeFromLocation();

  const filters = useMemo(
    () => mappers[type].fromSearchParams.toDefinition(searchParams),
    [searchParams]
  );
  const setFilters = (filters: FilterDefinition[]) => {
    const fromAccount = searchParams.get("fromAccount");
    const fromCountry = searchParams.get("fromCountry");
    const fromLocation = searchParams.get("fromLocation");
    const fromRoom = searchParams.get("fromRoom");
    const accountId = searchParams.get("accountId");
    const orderBy = searchParams.get("orderBy");
    const tab = searchParams.get("tab");

    const filterParams = mappers[type].fromDefinition.toSearchParams(filters);
    if (fromAccount) filterParams.append("fromAccount", fromAccount);
    if (fromCountry) filterParams.append("fromCountry", fromCountry);
    if (fromLocation) filterParams.append("fromLocation", fromLocation);
    if (fromRoom) filterParams.append("fromRoom", fromRoom);
    if (accountId && (type === "Users" || type === "AuditTrails"))
      filterParams.append("accountId", accountId);
    if (orderBy) filterParams.append("orderBy", orderBy);
    if (tab) filterParams.append("tab", tab);
    if (filterParams.getAll.length > 0 && searchTypeFromLocation !== undefined)
      filterParams.append("mode", SearchFilterMode.FILTER);
    setSearchParams(filterParams, { replace: true, state });
  };

  return [filters, setFilters];
};

export const useUserFilters = (): [
  value: LocalStorageFilters,
  setter: (value: LocalStorageFilters) => void
] => {
  const [userFilters, setUserFilters] =
    useLocalStorage<LocalStorageFilters>(FILTERS);
  const activeFilters = (
    Object.entries(DEFAULT_USER_FILTERS) as [
      key: keyof LocalStorageFilters,
      value: LocalStorageFilters[keyof LocalStorageFilters]
    ][]
  ).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: userFilters?.[key] ?? value,
    }),
    {} as LocalStorageFilters
  );

  return [activeFilters, setUserFilters];
};

export const useUserFilter = <K extends keyof LocalStorageFilters>(
  setting: K
): [
  value: LocalStorageFilters[K],
  setter: (value: LocalStorageFilters[K]) => void
] => {
  const [userSettings, setUserSettings] = useUserFilters();
  const setSetting = useCallback(
    (value: LocalStorageFilters[K]) =>
      setUserSettings({
        ...userSettings,
        [setting]: value,
      }),
    [userSettings, setting]
  );

  return [userSettings[setting], setSetting];
};

export enum EntityType {
  DEVICE_CATALOG = "DEVICE_CATALOG",
  COUNTRY = "COUNTRY",
}
export const areLocalStorageFiltersApplicable = (
  filter?: FilterDefinition[],
  state?: any,
  entityType?: EntityType
) => {
  return (
    Boolean(filter) &&
    (!state?.previous ||
      state.previous.toLowerCase() === "configuration" ||
      entityType === EntityType.DEVICE_CATALOG ||
      entityType === EntityType.COUNTRY) &&
    !state?.fromWidget
  );
};
